import React from 'react';
import './conversation.scss';
import { ErrorHandler, LoadingIndicator, Sizes } from '../components';
import { IConversationMessage } from './interfaces/state';
import { getFormattedLocalDateTime } from '../util/get-formatted-date';

export interface IConversationSectionProps {
  sectionId: number;
  error: Error;
  sectionName: string;
  isExpanded: boolean;
  isFirstSection: boolean;
  loading: boolean;
  messages: IConversationMessage[];
  currentMessage: string;
  loggedInUserDisplayName: string;
  readOnly: boolean;
  onSubmit(event: React.FormEvent<HTMLFormElement>): void;
  onMessageChange(event: React.ChangeEvent<HTMLInputElement>): void;
  submitButtonName: string;
  placeHolderText: string;
  articleRef(article: HTMLElement): void,
  enterMessageRef(textBox: HTMLInputElement): void
  onRetryClick(event: React.MouseEvent<HTMLElement, MouseEvent>): void;
};

export const ConversationSection = (props: IConversationSectionProps) => {
  const [isExpanded, setIsExpanded] = React.useState(props.isExpanded);

  const displayMessage = (message: IConversationMessage) => {
    const className = `message ${props.loggedInUserDisplayName === message.createdByDisplayName ? 'text-right' : 'text-left'}`;
    return (
      <div data-qa={`display-message-${props.sectionId}-${message.id}`} className={className} key={message.id}>
        <label className="createdByDisplayName">{message.createdByDisplayName}</label>
        <label className="createdDate">{getFormattedLocalDateTime(message.createdDate)}</label>
        <div className="content">
          {message.content}
        </div>
      </div>
    );
  };

  const displayUnreadSeparator = () => {
    return (
      <div data-qa="unread-separator" className="unreadSeparator">
        <hr />
        <span>Unread Messages</span>
      </div>
    );
  };

  const renderMessages = () => {
    if (props.error) {
      return (
        <div className="alert">
          <p className="alert-danger">An error occurred trying to load this conversation.</p>
          <div className="alert-danger">Try reloading the conversation.</div>
        </div>
      );
    }
    const read = props.messages.filter(msg => !msg.isUnread).map(displayMessage);
    const unread = props.messages.filter(msg => msg.isUnread).map(displayMessage);
    return (
      <>
        {read}
        {unread.length ? displayUnreadSeparator() : null}
        {unread}
      </>
    );
  };

  const displayInput = () => {
    if (props.readOnly === true) {
      return null;
    }
    return (
      <form data-qa="conversation-form" onSubmit={props.onSubmit}>
        <input
          data-qa={`enter-message-${props.sectionId}`}
          type="text"
          value={props.currentMessage}
          onChange={props.onMessageChange}
          placeholder={props.placeHolderText}
          ref={props.enterMessageRef}
        />
        <button
          type="submit"
          className="btn btn-primary"
          data-qa={`sendConversationBtn-${props.sectionId}`}
        >{props.submitButtonName}</button>
      </form>
    );
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const renderConversation = () => {
    if (!isExpanded) {
      return null;
    }

    return (
      <div style={{ paddingBottom: 20 }}>
        <article id="conversationArticle" ref={props.articleRef}>
          {
            props.loading === true
              ? <LoadingIndicator data-qa="loading-indicator" size={Sizes.Small} />
              : <div data-qa={`messages_${props.sectionId}`}>{renderMessages()}</div>
          }
        </article>
        {displayInput()}
      </div>
    );
  }

  const borderBottom = props.isFirstSection && !isExpanded ? "0px" : "1px solid #bbb";
  return (
    <div className="conversationWrapper">
      <div
        data-qa={`conversation_${props.sectionId}`}
        className="conversation"
        style={{ maxHeight: "none" }}>
        <ErrorHandler error={props.error} onRetryClick={props.onRetryClick}>
          { /* TODO: Move style to conversation.scss when change request & hold request are using conversation sections */}
          <button
            className="btn btn-secondary btn-block"
            style={{
              border: "0px",
              borderBottom,
              borderTop: "1px solid #bbb",
              borderRadius: 0,
              padding: 0,
            }}
            onClick={toggleExpanded}
            title="Toggle Conversation">
            <header
              style={{
                borderBottom: "0px",
                backgroundColor: "#ffffff"
              }}>
              <div className="flex">
                <div data-qa="level-title" className="bold">
                  {props.sectionName} ({props.messages.length})
                </div>
              </div>
              <div className={`ns-icon ns-${isExpanded ? 'collapse' : 'expand'}`} />
            </header>
          </button>
          {renderConversation()}
        </ErrorHandler>
      </div>
    </div >
  );
};
